import type { GraphEdge, WorkflowGraph } from "./workflowGraph";

/**
 * Where the edges that skip steps run (P-14; review round 2, R1 and R2), decided once for
 * the layout and the routes alike. Each runs under its level's row in a lane of its own,
 * and beside the cards in a channel of its own: it leaves its source to the right, drops to
 * its lane, runs under the cards between, and rises to its target from the left. The gap
 * after a pipeline card is wide enough for the channels of the edges leaving that card and
 * of those entering the next, so no edge passes through a card however many there are.
 * Pure: from the graph alone, before anything is placed.
 */

export const SKIP_CHANNELS = {
  /** Under the lowest card of the row: the first lane. */
  laneGap: 22,
  /** Between lanes. */
  laneSpacing: 10,
  /** Under the lowest lane, so its line and arrow are not at the level's edge. */
  laneMargin: 12,
  /** Between a card and its nearest channel. */
  runMargin: 10,
  /** Between channels beside one card. */
  runSpacing: 6,
  /** In the middle of a gap, kept free for an edge to the next card to turn in. */
  turn: 16,
} as const;

export interface SkipPlan {
  /** By edge ID: its lane under the row, 0 nearest it; shorter spans nearer. */
  readonly lane: ReadonlyMap<string, number>;
  /** By edge ID: its channel right of its source's card, 0 nearest the card. */
  readonly leaving: ReadonlyMap<string, number>;
  /** By edge ID: its channel left of its target's card, 0 nearest the card. */
  readonly entering: ReadonlyMap<string, number>;
  /** By level (`null`: the top level): how many lanes run under its row. */
  readonly lanes: ReadonlyMap<string | null, number>;
  /** By node ID at its level: how many channels lie right of it, and left of it. */
  readonly channels: ReadonlyMap<string, { readonly leaving: number; readonly entering: number }>;
}

export function skipPlan(graph: WorkflowGraph): SkipPlan {
  const parentOf = new Map(graph.nodes.map((node) => [node.id, node.parentId]));
  const atLevel = (id: string, level: string | null) => nodeAtLevel(parentOf, id, level);
  const order = new Map<string, number>();
  const counted = new Map<string | null, number>();
  for (const node of graph.nodes) {
    if (node.band === "main") {
      const index = counted.get(node.parentId) ?? 0;
      order.set(node.id, index);
      counted.set(node.parentId, index + 1);
    }
  }
  const byLevel = new Map<string | null, GraphEdge[]>();
  for (const edge of graph.edges) {
    if (edge.route === "skip") {
      const level = parentOf.get(edge.target) ?? null;
      byLevel.set(level, [...(byLevel.get(level) ?? []), edge]);
    }
  }
  const lane = new Map<string, number>();
  const lanes = new Map<string | null, number>();
  const leaving = new Map<string, number>();
  const entering = new Map<string, number>();
  const channels = new Map<string, { leaving: number; entering: number }>();
  for (const [level, edges] of byLevel) {
    const ends = (edge: GraphEdge) => ({ source: atLevel(edge.source, level), target: atLevel(edge.target, level) });
    const span = (edge: GraphEdge) => {
      const { source, target } = ends(edge);
      return (order.get(target) ?? 0) - (order.get(source) ?? 0);
    };
    const sorted = [...edges].sort((left, right) => span(left) - span(right) || (order.get(ends(left).source) ?? 0) - (order.get(ends(right).source) ?? 0));
    sorted.forEach((edge, index) => lane.set(edge.id, index));
    lanes.set(level, sorted.length);
    // Beside a card the deepest lane runs nearest it, so no run crosses another's lane.
    const byDepth = [...sorted].reverse();
    for (const edge of byDepth) {
      const { source, target } = ends(edge);
      const at = (id: string) => channels.get(id) ?? { leaving: 0, entering: 0 };
      const from = at(source);
      leaving.set(edge.id, from.leaving);
      channels.set(source, { ...from, leaving: from.leaving + 1 });
      const into = at(target);
      entering.set(edge.id, into.entering);
      channels.set(target, { ...into, entering: into.entering + 1 });
    }
  }
  return { lane, leaving, entering, lanes, channels };
}

/** The node at `level` that holds `id`: itself, or the open frame it is drawn inside. */
export function nodeAtLevel(parentOf: ReadonlyMap<string, string | null>, id: string, level: string | null): string {
  let current = id;
  for (;;) {
    const parent = parentOf.get(current);
    if (parent === undefined || parent === null || parent === level) {
      return current;
    }
    current = parent;
  }
}

/** The depth the lanes of `count` skip edges take under their row; 0 without any. */
export function skipLanesDepth(count: number): number {
  return count === 0 ? 0 : SKIP_CHANNELS.laneGap + (count - 1) * SKIP_CHANNELS.laneSpacing + SKIP_CHANNELS.laneMargin;
}

/**
 * How wide the gap between two neighbouring pipeline cards must be for its channels: the
 * leaving ones beside the left card, the entering ones beside the right card, and the turn
 * between, kept in the middle. At least `minimum`.
 */
export function channelGap(plan: SkipPlan, left: string, right: string, minimum: number): number {
  const side = Math.max(plan.channels.get(left)?.leaving ?? 0, plan.channels.get(right)?.entering ?? 0);
  return side === 0 ? minimum : Math.max(minimum, 2 * (SKIP_CHANNELS.runMargin + side * SKIP_CHANNELS.runSpacing) + SKIP_CHANNELS.turn);
}
